'use client';

import { faLocationDot, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useParams, usePathname } from 'next/navigation';
import { useState } from 'react';

import { CaptainNotificationBannerSkeleton } from '@/components/molecules/skeletons/CaptainNotificationBannerSkeleton';
import { PermissionRole } from '@/entities/PermissionRoles';
import { useCaptainNotificationBannerQuery } from '@/hooks/banner/useCaptainNotificationBannerQuery';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { usePermissionStore } from '@/store/permission-store';
import { DateUtil } from '@/utils/date';
import { getLastPathSegment } from '@/utils/utils';

export const CaptainNotificationBanner = () => {
	const teamLeagueInfo = useTeamLeagueInfo();
	const permissionStore = usePermissionStore();
	const pathname = usePathname();
	const params = useParams<{
		teamLeagueId: string;
	}>();

	const [isExpanded, setIsExpanded] = useState(false);
	const [dismissedBannerIds, setDismissedBannerIds] = useState<string[]>([]);
	const isTeamCaptain = permissionStore.userPermission === PermissionRole.TEAM_CAPTAIN;

	const currentRoute = getLastPathSegment(pathname);
	const showBanner = teamLeagueInfo.isSeasonActive && teamLeagueInfo.seasonStandingsExist ? currentRoute === '/standings' : currentRoute === '/';

	const { data: bannerInfo, isLoading: isLoadingBannerInfo } = useCaptainNotificationBannerQuery({ isEnabled: isTeamCaptain && showBanner });

	if (!showBanner || !isTeamCaptain) return null;

	if (isLoadingBannerInfo) {
		return <CaptainNotificationBannerSkeleton />;
	}

	if (bannerInfo?.statusCode && bannerInfo?.statusCode !== 200) {
		return null;
	}

	const upucomingMatchups = bannerInfo?.data?.results?.upcomingMatchups ?? [];
	const visibleMatchups = upucomingMatchups.filter((matchup) => !dismissedBannerIds.includes(matchup.uuid));

	if (visibleMatchups.length === 0) return null;

	const shouldStack = visibleMatchups.length > 1 && !isExpanded;

	return (
		<>
			{!!isExpanded && (
				<div
					className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
					role="presentation"
					onClick={() => setIsExpanded(false)}
				/>
			)}

			<div className="relative w-full px-4 sm:px-0">
				{!!isExpanded && <CaptainBannerSpacer />}

				<div
					className={clsx('transition-all duration-300', {
						'absolute top-0 left-0 w-full flex flex-col gap-4 z-50': isExpanded,
						relative: !isExpanded
					})}
					role="presentation"
					onClick={() => {
						if (shouldStack) setIsExpanded(true);
					}}
				>
					<div className={shouldStack ? 'relative' : 'flex flex-col gap-4 px-4 sm:px-0'}>
						{visibleMatchups.map((matchup, index) => {
							const abbreviation = matchup?.timezone?.abbreviation;
							const matchupStartDate = DateUtil.formatUTCInTimezone(
								matchup.plannedStartDate,
								'MMM D YYYY h:mm A',
								matchup?.timezone?.ianaTzIdentifier
							);
							const showTodayBadge = dayjs.utc(matchup.plannedStartDate).isSame(dayjs(), 'day');

							const positionClass = !shouldStack || index === 0 ? 'relative' : 'absolute top-0 left-0';
							const stackStyles = shouldStack
								? {
										transform: `translateY(-${index * 12}px)`,
										zIndex: 30 - index,
										width: '100%',
										height: '100%'
									}
								: {};

							return (
								<div
									key={matchup.uuid || index}
									style={stackStyles}
									className={`
                                        ${positionClass}
                                        flex flex-col sm:flex-row gap-4 sm:gap-6 justify-between items-center w-full rounded-xl border border-orange-500 bg-[#FDFDFD] text-black shadow-sm p-4
                                        transition-all duration-300
                                        ${shouldStack ? 'cursor-pointer' : ''}
                                    `}
								>
									<div
										className="absolute top-4 right-4 sm:hidden text-gray-400 hover:text-gray-600 transition-colors p-1 cursor-pointer"
										role="presentation"
										onClick={(e) => {
											e.stopPropagation();
											setDismissedBannerIds((prev) => [...prev, matchup.uuid]);
										}}
									>
										<FontAwesomeIcon icon={faXmark} className="size-5" />
									</div>

									<div className="flex flex-col gap-0.5 w-full sm:w-auto">
										<p className="text-gray-700 text-base not-italic font-bold leading-6 pr-6 sm:pr-0">
											You can now set your lineup. You have until {matchupStartDate} {abbreviation} to complete your lineup!
										</p>
										<div className="flex gap-1 text-sm not-italic font-medium leading-5 text-gray-500 flex-wrap">
											{!!showTodayBadge && <span>Today - </span>}
											<span>{matchupStartDate} </span>
											{!!abbreviation && <span>{abbreviation} </span>}
											{!!matchup.venue && (
												<div className="flex items-center gap-1">
													<span> | </span>
													<FontAwesomeIcon icon={faLocationDot} className="size-4 text-gray-500" />
													<span className="text-sm font-medium text-gray-500">{matchup.venue}</span>
												</div>
											)}
										</div>
										<div className="flex items-center gap-1.5 text-gray-700 text-base not-italic font-semibold leading-6">
											<div className="size-2 bg-orange-500 rounded-full" />
											<span>
												{matchup.teamTwoTitle} @ {matchup.teamOneTitle}
											</span>
										</div>
									</div>

									<div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start">
										<CustomLink
											href={`/team-leagues/${params.teamLeagueId}/matchup/${matchup.uuid}/lineups`}
											className="w-full sm:w-auto"
											onClick={(e) => {
												e.stopPropagation();
											}}
										>
											<Button type="button" size="default" className="w-full sm:w-auto font-semibold text-sm text-white">
												Set lineup
											</Button>
										</CustomLink>

										<div
											className="hidden sm:flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors p-1 cursor-pointer"
											role="presentation"
											onClick={(e) => {
												e.stopPropagation();
												setDismissedBannerIds((prev) => [...prev, matchup.uuid]);
											}}
										>
											<FontAwesomeIcon icon={faXmark} className="size-5" />
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</>
	);
};

const CaptainBannerSpacer = () => (
	<div className="invisible pointer-events-none opacity-0 pt-8">
		<div className="flex flex-col sm:flex-row justify-between items-center w-full rounded-xl border p-4">
			<div className="flex flex-col gap-0.5">
				<div className="h-3 w-48" />
				<div className="h-3 w-32" />
				<div className="h-4 w-40" />
			</div>
			<div className="h-10 w-28" />
		</div>
	</div>
);
